import React from 'react';
import { render, cleanup, act } from '@testing-library/react';
import { withWrapper } from '../testWrapper';
import { captureComponentSnapshot, createLogCapture } from '../snapshotCapture';

function assert(condition, message) {
  if (!condition) throw new Error(message);
}

async function renderComponent(element, assertions, waitMs = 200) {
  const logCapture = createLogCapture();
  let snapshot = null;
  try {
    let result;
    await act(async () => { result = render(element); });
    await act(async () => { await new Promise(r => setTimeout(r, waitMs)); });
    await assertions(result);
    snapshot = captureComponentSnapshot(result.container);
  } finally {
    cleanup();
    logCapture.restore();
  }
  return { snapshot, logs: logCapture.getLogs() };
}

// ─── Mock data ────────────────────────────────────────────────────────────────

const MOCK_PROJECT = { id: 1, uuid: 'p-test-1', name: 'Test Project Alpha' };

const MOCK_ARTICLE = {
  id: 1,
  article_title: 'Impact of Qualitative Methods on Research Quality',
  article_details: 'This article explores how qualitative approaches enhance research validity.',
  article_pages: 8,
  article_prompt: '',
  sections: [{ id: 1 }, { id: 2 }, { id: 3 }],
  created_at: new Date().toISOString(),
  status: 'completed',
};

const MOCK_ARTICLE_2 = {
  id: 2,
  article_title: 'Thematic Analysis in Social Research',
  article_details: 'A review of thematic analysis frameworks used in social science.',
  article_pages: 5,
  article_prompt: '',
  sections: [{ id: 4 }],
  created_at: new Date().toISOString(),
  status: 'completed',
};

export const articleTests = [

  // ─── ArticleCard ──────────────────────────────────────────────────────────────

  {
    id: 'article-card-renders-title',
    name: 'ArticleCard: renders article title',
    run: async () => {
      const ArticleCard = require('../../Components/Articles/ArticleCard').default;
      return await renderComponent(
        withWrapper(
          <ArticleCard article={MOCK_ARTICLE} projectUuid="p-test-1" />,
          '/project/p-test-1/articles'
        ),
        async ({ container }) => {
          assert(container.firstChild !== null, 'ArticleCard must render');
          assert(
            container.textContent.includes(MOCK_ARTICLE.article_title),
            `ArticleCard must show article title "${MOCK_ARTICLE.article_title}"`
          );
        }
      );
    },
  },

  {
    id: 'article-card-shows-pages-and-sections',
    name: 'ArticleCard: shows pages and sections count',
    run: async () => {
      const ArticleCard = require('../../Components/Articles/ArticleCard').default;
      return await renderComponent(
        withWrapper(
          <ArticleCard article={MOCK_ARTICLE} projectUuid="p-test-1" />,
          '/project/p-test-1/articles'
        ),
        async ({ container }) => {
          assert(
            container.textContent.includes(String(MOCK_ARTICLE.article_pages)),
            `ArticleCard must show page count "${MOCK_ARTICLE.article_pages}"`
          );
          assert(
            container.textContent.includes(String(MOCK_ARTICLE.sections.length)),
            `ArticleCard must show sections count "${MOCK_ARTICLE.sections.length}"`
          );
        }
      );
    },
  },

  {
    id: 'article-card-open-button',
    name: 'ArticleCard: renders Open button',
    run: async () => {
      const ArticleCard = require('../../Components/Articles/ArticleCard').default;
      return await renderComponent(
        withWrapper(
          <ArticleCard article={MOCK_ARTICLE} projectUuid="p-test-1" />,
          '/project/p-test-1/articles'
        ),
        async ({ container }) => {
          const btn = container.querySelector('button');
          assert(btn !== null, 'ArticleCard must render an Open button');
        }
      );
    },
  },

  // ─── CreateArticleModal ───────────────────────────────────────────────────────

  {
    id: 'create-article-modal-renders',
    name: 'CreateArticleModal: renders dialog with form fields',
    run: async () => {
      const CreateArticleModal = require('../../Components/Articles/CreateArticleModal').default;
      return await renderComponent(
        withWrapper(
          <CreateArticleModal
            projectId={1}
            onClose={() => {}}
            onSuccess={() => {}}
          />,
          '/project/p-test-1/articles'
        ),
        async () => {
          const dialog = document.body.querySelector('[role="dialog"]');
          assert(dialog !== null, 'CreateArticleModal dialog must render');
          assert(document.body.querySelector('#title') !== null,   'title input must render');
          assert(document.body.querySelector('#details') !== null,  'details textarea must render');
          assert(document.body.querySelector('#pages') !== null,    'pages input must render');
        }
      );
    },
  },

  {
    id: 'create-article-modal-edit-mode',
    name: 'CreateArticleModal: renders in edit mode pre-filled with article data',
    run: async () => {
      const CreateArticleModal = require('../../Components/Articles/CreateArticleModal').default;
      return await renderComponent(
        withWrapper(
          <CreateArticleModal
            projectId={1}
            article={MOCK_ARTICLE}
            onClose={() => {}}
            onSuccess={() => {}}
          />,
          '/project/p-test-1/articles'
        ),
        async () => {
          const dialog = document.body.querySelector('[role="dialog"]');
          assert(dialog !== null, 'CreateArticleModal dialog must render in edit mode');
          const titleInput = document.body.querySelector('#title');
          assert(titleInput !== null, 'title input must render in edit mode');
          assert(
            titleInput.value === MOCK_ARTICLE.article_title,
            `title input must be pre-filled with "${MOCK_ARTICLE.article_title}", got "${titleInput.value}"`
          );
        }
      );
    },
  },

  // ─── ArticlesTab ─────────────────────────────────────────────────────────────

  {
    id: 'articles-tab-empty-state',
    name: 'ArticlesTab: renders empty state when API returns no articles',
    run: async () => {
      const ArticlesTab = require('../../Components/Articles/ArticlesTab').default;
      const axios = require('axios');
      const originalAdapter = axios.defaults.adapter;
      axios.defaults.adapter = (config) => new Promise(resolve => setTimeout(() => resolve({
        data: { articles: [] },
        status: 200, statusText: 'OK',
        headers: { 'content-type': 'application/json' },
        config, request: {},
      }), 30));

      const logCapture = createLogCapture();
      let snapshot = null;
      try {
        let result;
        await act(async () => { result = render(withWrapper(<ArticlesTab project={MOCK_PROJECT} />, '/project/p-test-1/articles')); });
        await act(async () => { await new Promise(r => setTimeout(r, 400)); });
        const container = result.container;
        const emptyState = container.querySelector('[data-testid="articles-empty"]');
        assert(emptyState !== null, 'articles-empty must render when no articles returned');
        const newBtn = container.querySelector('[data-testid="articles-new-btn"]');
        assert(newBtn !== null, 'articles-new-btn must render');
        snapshot = captureComponentSnapshot(container);
      } finally {
        cleanup();
        axios.defaults.adapter = originalAdapter;
        logCapture.restore();
      }
      return { snapshot, logs: logCapture.getLogs() };
    },
  },

  {
    id: 'articles-tab-with-articles',
    name: 'ArticlesTab: renders article cards when API returns articles',
    run: async () => {
      const ArticlesTab = require('../../Components/Articles/ArticlesTab').default;
      const axios = require('axios');
      const originalAdapter = axios.defaults.adapter;
      const mockArticles = [MOCK_ARTICLE, MOCK_ARTICLE_2];
      axios.defaults.adapter = (config) => new Promise(resolve => setTimeout(() => resolve({
        data: { articles: mockArticles },
        status: 200, statusText: 'OK',
        headers: { 'content-type': 'application/json' },
        config, request: {},
      }), 30));

      const logCapture = createLogCapture();
      let snapshot = null;
      try {
        let result;
        await act(async () => { result = render(withWrapper(<ArticlesTab project={MOCK_PROJECT} />, '/project/p-test-1/articles')); });
        await act(async () => { await new Promise(r => setTimeout(r, 400)); });
        const container = result.container;
        assert(
          container.querySelector('[data-testid="articles-tab-container"]') !== null,
          'articles-tab-container must render'
        );
        assert(
          container.textContent.includes(MOCK_ARTICLE.article_title),
          `articles list must show "${MOCK_ARTICLE.article_title}"`
        );
        assert(
          container.textContent.includes(MOCK_ARTICLE_2.article_title),
          `articles list must show "${MOCK_ARTICLE_2.article_title}"`
        );
        assert(
          container.querySelector('[data-testid="articles-empty"]') === null,
          'articles-empty must NOT render when articles are present'
        );
        snapshot = captureComponentSnapshot(container);
      } finally {
        cleanup();
        axios.defaults.adapter = originalAdapter;
        logCapture.restore();
      }
      return { snapshot, logs: logCapture.getLogs() };
    },
  },

];
